import type {
  SavedCircuitConfigurationIdentity,
  SavedCircuitLapSet,
  SavedCircuitLapWindow,
  SessionLapTimingOverlay,
  SessionLapTimingWindow,
  TelemetrySessionContext,
} from '@kangaroo/shared';
import { savedCircuitConfigurationKey } from '@kangaroo/shared';

import type { CircuitMapOverlay } from './circuitMapOverlay';
import type { StartLineSuggestion } from './trackEnrichment';
import {
  resliceLapsAtStartLine,
  reslicedFlyingLaps,
  reslicedLapKindCounts,
  type ResliceLapsResult,
  type ReslicedLapWindow,
} from './trackEnrichment';
import {
  formatTraceClock,
  gpsPointsInWindow,
  lapWindow,
  resolveRawLoggerLaps,
  sessionRecordedAtMs,
  type LapTableRow,
  type MyChronLapRecord,
  type MyChronSessionExport,
} from './telemetrySession';

export { reslicedFlyingLaps, reslicedLapKindCounts };
export type { ResliceLapsResult, ReslicedLapWindow };

export type ConfigurationScopedCircuitReslice = {
  configurationKey: string;
  reslice: ResliceLapsResult;
};

export function bindCircuitResliceToConfiguration(
  reslice: ResliceLapsResult,
  configuration: SavedCircuitConfigurationIdentity | null | undefined,
): ConfigurationScopedCircuitReslice | null {
  const configurationKey = savedCircuitConfigurationKey(configuration);
  return configurationKey ? { configurationKey, reslice } : null;
}

export function circuitResliceForConfiguration(
  scopedReslice: ConfigurationScopedCircuitReslice | null,
  configuration: SavedCircuitConfigurationIdentity | null | undefined,
): ResliceLapsResult | null {
  const configurationKey = savedCircuitConfigurationKey(configuration);
  return configurationKey && scopedReslice?.configurationKey === configurationKey
    ? scopedReslice.reslice
    : null;
}

export function sessionContextForCircuitConfiguration(
  context: TelemetrySessionContext | null | undefined,
  coherentSavedCircuitLapSet: SavedCircuitLapSet | null,
): TelemetrySessionContext | null | undefined {
  if (!context) {
    return context;
  }

  const overlayCircuitLapSet = context.lapTimingOverlay?.circuitLapSet;
  const overlayMatchesSavedSet = Boolean(
    coherentSavedCircuitLapSet &&
    overlayCircuitLapSet &&
    overlayCircuitLapSet.contributionId === coherentSavedCircuitLapSet.contributionId &&
    overlayCircuitLapSet.savedAt === coherentSavedCircuitLapSet.savedAt,
  );
  const lapTimingOverlay = context.lapTimingOverlay
    ? {
        ...context.lapTimingOverlay,
        circuitLapSet: overlayMatchesSavedSet ? overlayCircuitLapSet! : null,
      }
    : context.lapTimingOverlay;

  if (
    context.circuitLapSet === coherentSavedCircuitLapSet &&
    lapTimingOverlay === context.lapTimingOverlay
  ) {
    return context;
  }

  return {
    ...context,
    circuitLapSet: coherentSavedCircuitLapSet,
    lapTimingOverlay,
  };
}

export function buildCircuitLapReslice(
  session: MyChronSessionExport,
  circuitOverlay: CircuitMapOverlay,
): ResliceLapsResult {
  if (!circuitOverlay.startFinishLine) {
    return { laps: [], crossingsMs: [] };
  }

  const polyline =
    circuitOverlay.referenceTrackGroups[0]?.map((point) => ({ lat: point.lat, lon: point.lon })) ??
    [];

  return resliceLapsAtStartLine(session, circuitOverlay.startFinishLine as StartLineSuggestion, {
    referencePolyline: polyline.length >= 2 ? polyline : undefined,
  });
}

export function reslicedLapsToMyChronRecords(laps: ReslicedLapWindow[]): MyChronLapRecord[] {
  return laps.map((lap) => ({
    segment: 0,
    lapNumber: lap.lapNumber,
    durationMs: Math.round(lap.durationSec * 1000),
    endTimeMs: Math.round(lap.endMs),
  }));
}

export function resolveSessionRawLaps(
  session: MyChronSessionExport,
  circuitOverlay?: CircuitMapOverlay | null,
): MyChronLapRecord[] {
  const fromLogger = resolveRawLoggerLaps(session);
  if (fromLogger.length > 0) {
    return fromLogger;
  }

  if (circuitOverlay?.startFinishLine) {
    const reslice = buildCircuitLapReslice(session, circuitOverlay);
    if (reslice.laps.length > 0) {
      return reslicedLapsToMyChronRecords(reslice.laps);
    }
  }

  return [];
}

export function reslicedLapToSavedWindow(lap: ReslicedLapWindow): SavedCircuitLapWindow {
  const startMs = Math.round(lap.startMs);
  const endMs = Math.round(lap.endMs);
  const durationMs = Math.max(1, endMs - startMs);

  return {
    lapNumber: lap.lapNumber,
    startMs,
    endMs,
    durationSec: durationMs / 1000,
    kind: lap.kind,
    complete: lap.complete,
  };
}

export function savedWindowToReslicedLap(window: SavedCircuitLapWindow): ReslicedLapWindow {
  return {
    lapNumber: window.lapNumber,
    startMs: window.startMs,
    endMs: window.endMs,
    durationSec: window.durationSec,
    complete: window.complete,
    kind: window.kind,
  };
}

export function buildSavedCircuitLapSet(
  circuitOverlay: CircuitMapOverlay,
  reslice: ResliceLapsResult,
): SavedCircuitLapSet {
  const laps = reslice.laps
    .map(reslicedLapToSavedWindow)
    .filter((lap) => lap.endMs > lap.startMs && lap.durationSec > 0);

  return {
    contributionId: circuitOverlay.contributionId,
    circuitName: circuitOverlay.name,
    savedAt: new Date().toISOString(),
    ...(circuitOverlay.configuration ? { configuration: circuitOverlay.configuration } : {}),
    laps,
  };
}

function utcIsoForOffset(anchorMs: number | null, offsetMs: number) {
  if (anchorMs === null || !Number.isFinite(anchorMs)) {
    return null;
  }

  return new Date(anchorMs + Math.round(offsetMs)).toISOString();
}

function buildTimingWindow(
  anchorMs: number | null,
  lap: {
    lapNumber: number;
    startMs: number;
    endMs: number;
    durationSec: number;
    kind?: SessionLapTimingWindow['kind'];
    complete?: boolean;
  },
): SessionLapTimingWindow {
  return {
    lapNumber: lap.lapNumber,
    startMs: Math.round(lap.startMs),
    endMs: Math.round(lap.endMs),
    durationSec: lap.durationSec,
    utcStartIso: utcIsoForOffset(anchorMs, lap.startMs),
    utcEndIso: utcIsoForOffset(anchorMs, lap.endMs),
    ...(lap.kind ? { kind: lap.kind } : {}),
    ...(lap.complete !== undefined ? { complete: lap.complete } : {}),
  };
}

export function buildRawLapTimingWindows(
  session: MyChronSessionExport,
  anchorMs: number | null = sessionRecordedAtMs(session),
  circuitOverlay?: CircuitMapOverlay | null,
): SessionLapTimingWindow[] {
  const normalizedAnchorMs = anchorMs !== null && Number.isFinite(anchorMs) ? anchorMs : null;

  return resolveSessionRawLaps(session, circuitOverlay).map((lap) => {
    const window = lapWindow(lap);
    return buildTimingWindow(normalizedAnchorMs, {
      lapNumber: lap.lapNumber,
      startMs: window.startMs,
      endMs: window.endMs,
      durationSec: lap.durationMs / 1000,
    });
  });
}

export function hydrateSessionLapTimingOverlay(
  overlay: SessionLapTimingOverlay,
  session: MyChronSessionExport | null | undefined,
  circuitOverlay?: CircuitMapOverlay | null,
): SessionLapTimingOverlay {
  if (!session || (overlay.rawLaps?.length ?? 0) > 0) {
    return overlay;
  }

  const anchorMs = overlay.anchorMs ?? sessionRecordedAtMs(session);
  const normalizedAnchorMs = anchorMs !== null && Number.isFinite(anchorMs) ? anchorMs : null;
  const rawLaps = buildRawLapTimingWindows(session, normalizedAnchorMs, circuitOverlay);
  if (rawLaps.length === 0) {
    return overlay;
  }

  return {
    ...overlay,
    anchorMs: normalizedAnchorMs,
    anchorIso:
      overlay.anchorIso ??
      (normalizedAnchorMs === null ? null : new Date(normalizedAnchorMs).toISOString()),
    rawLaps,
  };
}

export function buildSessionLapTimingOverlay(
  session: MyChronSessionExport,
  circuitOverlay: CircuitMapOverlay,
  reslice: ResliceLapsResult,
  circuitLapSet: SavedCircuitLapSet,
): SessionLapTimingOverlay {
  const anchorMs = sessionRecordedAtMs(session);
  const normalizedAnchorMs = anchorMs !== null && Number.isFinite(anchorMs) ? anchorMs : null;

  return {
    generatedAt: new Date().toISOString(),
    anchorMs: normalizedAnchorMs,
    anchorIso: normalizedAnchorMs === null ? null : new Date(normalizedAnchorMs).toISOString(),
    rawLaps: buildRawLapTimingWindows(session, normalizedAnchorMs, circuitOverlay),
    circuitLapSet: {
      contributionId: circuitOverlay.contributionId,
      circuitName: circuitOverlay.name,
      savedAt: circuitLapSet.savedAt,
      laps: reslice.laps.map((lap) =>
        buildTimingWindow(normalizedAnchorMs, {
          lapNumber: lap.lapNumber,
          startMs: lap.startMs,
          endMs: lap.endMs,
          durationSec: lap.durationSec,
          kind: lap.kind,
          complete: lap.complete,
        }),
      ),
    },
  };
}

export function resliceFromSavedCircuitLapSet(saved: SavedCircuitLapSet): ResliceLapsResult {
  return {
    laps: saved.laps.map(savedWindowToReslicedLap),
    crossingsMs: [],
  };
}

export function buildReslicedLapManagementRows(
  session: MyChronSessionExport,
  laps: ReslicedLapWindow[],
): LapTableRow[] {
  const flying = laps.filter((lap) => lap.kind === 'flying');
  const bestLap = flying.reduce<ReslicedLapWindow | null>(
    (best, lap) => (!best || lap.durationSec < best.durationSec ? lap : best),
    null,
  );

  return laps.map((lap) => {
    const speeds = gpsPointsInWindow(session.gps, lap.startMs, lap.endMs).map(
      (point) => point.speed * 3.6,
    );
    const exclusionReason =
      lap.kind === 'flying'
        ? null
        : lap.kind === 'out'
          ? ('out' as const)
          : lap.kind === 'in'
            ? ('in' as const)
            : ('other' as const);

    return {
      lapNumber: lap.lapNumber,
      timeSec: lap.durationSec,
      gapSec:
        lap.kind === 'flying' && bestLap && lap.lapNumber !== bestLap.lapNumber
          ? lap.durationSec - bestLap.durationSec
          : null,
      sectors: null,
      topSpeedKph: speeds.length ? Math.max(...speeds) : null,
      isBest: Boolean(bestLap && lap.lapNumber === bestLap.lapNumber && lap.kind === 'flying'),
      excluded: lap.kind !== 'flying',
      exclusionReason,
    };
  });
}

export function formatReslicedLapLabel(lap: ReslicedLapWindow) {
  const time = formatTraceClock(lap.durationSec);
  if (lap.kind === 'out') {
    return lap.complete ? `L${lap.lapNumber} · Out · ${time}` : `Out · ${time}`;
  }
  if (lap.kind === 'in') {
    return `In · ${time}`;
  }
  if (lap.kind === 'crash') {
    return `Crash · ${time}`;
  }
  return `L${lap.lapNumber} · ${time}`;
}

export function formatResliceSummary(counts: ReturnType<typeof reslicedLapKindCounts>) {
  const parts: string[] = [];
  if (counts.flying > 0) {
    parts.push(`${counts.flying} flying`);
  }
  if (counts.out > 0) {
    parts.push(`${counts.out} out`);
  }
  if (counts.in > 0) {
    parts.push(`${counts.in} in`);
  }
  if (counts.crash > 0) {
    parts.push(`${counts.crash} crash`);
  }
  return parts.length > 0 ? parts.join(' · ') : '0 laps';
}

export function reslicedLapChipStyles(lap: ReslicedLapWindow, selected: boolean) {
  const palette = {
    flying: {
      base: 'rgba(168, 85, 247, 0.12)',
      selected: 'rgba(168, 85, 247, 0.28)',
      border: 'rgba(168, 85, 247, 0.25)',
      borderSelected: 'rgba(168, 85, 247, 0.55)',
      color: '#C4B5FD',
    },
    out: {
      base: 'rgba(245, 158, 11, 0.16)',
      selected: 'rgba(245, 158, 11, 0.32)',
      border: 'rgba(245, 158, 11, 0.35)',
      borderSelected: 'rgba(245, 158, 11, 0.55)',
      color: '#FBBF24',
    },
    in: {
      base: 'rgba(148, 163, 184, 0.14)',
      selected: 'rgba(148, 163, 184, 0.28)',
      border: 'rgba(148, 163, 184, 0.32)',
      borderSelected: 'rgba(148, 163, 184, 0.5)',
      color: '#CBD5E1',
    },
    crash: {
      base: 'rgba(239, 68, 68, 0.14)',
      selected: 'rgba(239, 68, 68, 0.28)',
      border: 'rgba(239, 68, 68, 0.35)',
      borderSelected: 'rgba(239, 68, 68, 0.55)',
      color: '#FCA5A5',
    },
  }[lap.kind];

  return {
    bgcolor: selected ? palette.selected : palette.base,
    color: palette.color,
    border: `1px solid ${selected ? palette.borderSelected : palette.border}`,
  };
}

export function buildReslicedRankPool(laps: ReslicedLapWindow[]) {
  return laps
    .filter((lap) => lap.kind === 'flying')
    .map((lap) => ({ lapNumber: lap.lapNumber, durationSec: lap.durationSec }));
}

export function selectedReslicedLapWindow(
  reslice: ResliceLapsResult,
  lapNumber: number,
): ReslicedLapWindow | null {
  return reslice.laps.find((lap) => lap.lapNumber === lapNumber) ?? null;
}

export function defaultReslicedFlyingLapNumber(reslice: ResliceLapsResult) {
  const flying = reslicedFlyingLaps(reslice);
  if (flying.length === 0) {
    return reslice.laps[0]?.lapNumber ?? 1;
  }

  return flying.reduce((best, lap) => (lap.durationSec < best.durationSec ? lap : best)).lapNumber;
}
